import { z } from 'zod'
import potentialSchema from '@/types/schema/kol/potential'
import { priceRangeSchema } from '@/types/schema/kol/price-range-schema'
import { performanceQuartileMetricsSchema } from '@/types/schema/performance-quartile-metrics-schema'
import { platformShortcodeSchema } from '@/types/schema/platform/platform-shortcode-schema'

export const kpiSchema = z.object({
  platform: platformShortcodeSchema.extract([
    'fb',
    'ig',
    'yt',
    'tiktok',
    'twitter',
    'threads',
  ]),
  follower: z.number(),
  avg_comment_3m: z.number().nullable(),
  avg_comment_6m: z.number().nullable(),
  avg_like_3m: z.number().nullable(),
  avg_like_6m: z.number().nullable(),
  avg_repost_and_quote_count_3m: z.number().nullable(),
  avg_repost_and_quote_count_6m: z.number().nullable(),
  like_comment_ratio_3m: z.number().nullable(),
  like_comment_ratio_6m: z.number().nullable(),
  interactive_3m: z.number().nullable(),
  interactive_6m: z.number().nullable(),
  interactive_rate_3m: z.number().nullable(),
  interactive_rate_6m: z.number().nullable(),
  avg_share_3m: z.number().nullable(),
  avg_share_6m: z.number().nullable(),
  post_count_3m: z.number(),
  post_count_6m: z.number().nullable(),
  fans_up_rate_count_3m: z.number(),
  fans_up_rate_count_6m: z.number().nullable(),
  fans_up_rate_3m: z.number().nullable(),
  fans_up_rate_6m: z.number().nullable(),
  avg_live_view_count_3m: z.number().nullable(),
  avg_live_view_count_6m: z.number().nullable(),
  avg_live_view_rate_6m: z.number().nullable(),
  avg_live_view_rate_3m: z.number().nullable(),
  avg_short_view_rate_6m: z.number().nullable(),
  avg_short_view_rate_3m: z.number().nullable(),
  avg_short_view_count_3m: z.number().nullable(),
  avg_short_view_count_6m: z.number().nullable(),
  avg_short_engagement_count_3m: z.number().nullable(),
  avg_short_engagement_count_6m: z.number().nullable(),
  avg_short_like_count_3m: z.number().nullable(),
  avg_short_like_count_6m: z.number().nullable(),
  avg_short_comment_count_3m: z.number().nullable(),
  avg_short_comment_count_6m: z.number().nullable(),
  avg_pv_3m: z.number().nullable(),
  avg_pv_6m: z.number().nullable(),
  avg_pv_rate_3m: z.number().nullable(),
  avg_pv_rate_6m: z.number().nullable(),
  post_frequency_3m: z.number().nullable(),
  post_frequency_6m: z.number().nullable(),
  post_word_of_mouth_volume_3m: z.number().nullable(),
  post_word_of_mouth_volume_6m: z.number().nullable(),
  video_word_of_mouth_volume_3m: z.number().nullable(),
  video_word_of_mouth_volume_6m: z.number().nullable(),
  avg_short_play_count_3m: z.number().nullable(),
  avg_short_play_count_6m: z.number().nullable(),
  avg_short_play_rate_3m: z.number().nullable(),
  avg_short_play_rate_6m: z.number().nullable(),
  avg_short_engagement_rate_3m: z.number().nullable(),
  avg_short_engagement_rate_6m: z.number().nullable(),
  min_image_estimated_reach_count_3m: z.number().nullable(),
  max_image_estimated_reach_count_3m: z.number().nullable(),
  min_image_estimated_reach_count_6m: z.number().nullable(),
  max_image_estimated_reach_count_6m: z.number().nullable(),
  min_video_estimated_reach_count_3m: z.number().nullable(),
  max_video_estimated_reach_count_3m: z.number().nullable(),
  min_video_estimated_reach_count_6m: z.number().nullable(),
  max_video_estimated_reach_count_6m: z.number().nullable(),
  min_short_estimated_reach_count_3m: z.number().nullable(),
  max_short_estimated_reach_count_3m: z.number().nullable(),
  min_short_estimated_reach_count_6m: z.number().nullable(),
  max_short_estimated_reach_count_6m: z.number().nullable(),
  score_3m: z.number().nullable(),
  score_6m: z.number().nullable(),
  sponsor_post_ratio_3m: z.number().nullable(),
  sponsor_post_ratio_6m: z.number().nullable(),
  audience: z
    .object({
      main_age: z
        .object({
          code: z.string(),
          weight: z.number(),
        })
        .nullable(),
      main_gender: z
        .object({
          code: z.string(),
          weight: z.number(),
        })
        .nullable(),
    })
    .nullable(),
  potential: potentialSchema.nullable(),
  is_crawler_running: z
    .boolean()
    .nullable()
    .describe('如果是 true, 代表已觸發爬蟲正在爬取中, 可以顯示訂閱通知按鈕'),
  is_crawler_triggered: z
    .boolean()
    .nullable()
    .describe('如果是 true, 代表 radar 已收集到觸發更新需求, 可以顯示更新中'),
  is_insufficient: z.boolean().nullable(),
  update_time: z
    .string()
    .describe('當前 kpi snapshot 的時間（user 所買的是什麼時候的資料）'),
  latest_data_updated_at: z
    .string()
    .nullable()
    .describe('此 kpi 在 Kolr server 的最後更新時間 (DC 更新時間)'),
  interactive_rate_performance_1m: performanceQuartileMetricsSchema,
  interactive_rate_performance_3m: performanceQuartileMetricsSchema,
  interactive_rate_performance_6m: performanceQuartileMetricsSchema,
  avg_pv_rate_performance_1m: performanceQuartileMetricsSchema,
  avg_pv_rate_performance_3m: performanceQuartileMetricsSchema,
  avg_pv_rate_performance_6m: performanceQuartileMetricsSchema,
  fans_up_rate_performance_1m: performanceQuartileMetricsSchema,
  fans_up_rate_performance_3m: performanceQuartileMetricsSchema,
  fans_up_rate_performance_6m: performanceQuartileMetricsSchema,
  avg_short_view_rate_performance_1m: performanceQuartileMetricsSchema,
  avg_short_view_rate_performance_3m: performanceQuartileMetricsSchema,
  avg_short_view_rate_performance_6m: performanceQuartileMetricsSchema,
  avg_comment_like_rate_performance_1m: performanceQuartileMetricsSchema,
  avg_comment_like_rate_performance_3m: performanceQuartileMetricsSchema,
  avg_comment_like_rate_performance_6m: performanceQuartileMetricsSchema,
  avg_short_engagement_rate_performance_1m: performanceQuartileMetricsSchema,
  avg_short_engagement_rate_performance_3m: performanceQuartileMetricsSchema,
  avg_short_engagement_rate_performance_6m: performanceQuartileMetricsSchema,
  quotations: z
    .object({
      post: priceRangeSchema.nullable().catch(null),
      video: priceRangeSchema.nullable().catch(null),
    })
    .nullable()
    .describe('Use quotationsSchema instead after api bug fixed'),
})
export type Kpi = z.infer<typeof kpiSchema>
